import { getSavedLang } from '@flexo/general';
import { store } from '@flexo/reducers';
import { Day } from 'date-fns';
import * as luxon from 'luxon';

const ENV = (import.meta as any).env;

function getBrowserLanguage() {
  const savedLang = getSavedLang();
  const fromSaved =
    (savedLang && (savedLang.split('_')[1] || savedLang.split('_')[0])) ||
    undefined;
  return (
    fromSaved ||
    navigator.language?.split('-')[0] ||
    ENV.VITE_APP_LOCALE ||
    'en'
  );
}

export function checkIfDayIsAvailable(day: Date, effectiveEndDate = getEffectiveEndDate()) {
  const startDate = getStartDate();

  // Check if day is before startDate
  if (
    startDate &&
    day <
      luxon.DateTime.fromJSDate(new Date(startDate)).startOf('day').toJSDate()
  ) {
    return false;
  }

  // Check if day is after endDate (or today if no endDate)
  const upperLimit = effectiveEndDate;
  if (day > luxon.DateTime.fromJSDate(upperLimit).startOf('day').toJSDate()) {
    return false;
  }

  return true;
}

export function checkItIsToday(selectedDate: [Date, Date]) {
  return (
    luxon.DateTime.fromJSDate(selectedDate[0]).startOf('day').toMillis() ===
    luxon.DateTime.fromJSDate(new Date()).startOf('day').toMillis()
  );
}

export function formatDay(selectedDate: [Date, Date]) {
  return luxon.DateTime.fromJSDate(selectedDate[0]).toFormat('dd/MM/yyyy');
}

export function formatWeek(selectedDate: [Date, Date]) {
  return `${luxon.DateTime.fromJSDate(selectedDate[0]).toFormat(
    'dd/MM/yyyy'
  )} - ${luxon.DateTime.fromJSDate(selectedDate[1])
    .minus({ days: 1 })
    .toFormat('dd/MM/yyyy')}`;
}

export function formatMonth(
  selectedDate: [Date, Date] | Date,
  locale?: string
) {
  const l = locale?.split('_')[1] || getBrowserLanguage();
  const d: any = Array.isArray(selectedDate)
    ? (selectedDate as any)?.[0]
    : selectedDate;
  return `${(d as any).toLocaleString(l as any, {
    month: 'long',
    year: 'numeric',
  })}`;
}

export function formatYear(selectedDate: [Date, Date]) {
  return `${selectedDate?.[0].getFullYear()}`;
}

export function formatDuoDecade(years: Array<Date>) {
  return `${years[0].getFullYear()} - ${years[years.length - 1].getFullYear()}`;
}

export function isSevenDaysApart(date1, date2) {
  const dt1 = luxon.DateTime.fromJSDate(date1);
  const dt2 = luxon.DateTime.fromJSDate(date2);

  const diffInDays = Math.abs(dt1.diff(dt2, 'days').days);

  return diffInDays === 7;
}

export function generateYearArrays(selectedDate) {
  const selectedYear = selectedDate[0].getFullYear(); // Anno della data selezionata
  const currentYear = new Date().getFullYear(); // Anno corrente

  // Determiniamo il primo anno per iniziare il calcolo
  let adjustedStartYear = selectedYear;

  // Assicuriamoci che il currentYear sia nella posizione corretta
  while ((adjustedStartYear + 10) % 12 !== currentYear % 12) {
    adjustedStartYear--;
  }

  const arrays: any = [];
  let startYear = adjustedStartYear;

  for (let i = 0; i < 3; i++) {
    // Creiamo 3 gruppi
    const yearGroup: any = [];

    for (let j = 0; j < 4; j++) {
      // Ogni gruppo contiene 4 anni
      yearGroup.push(new Date(startYear, 0, 1));
      startYear++;
    }

    arrays.push(yearGroup);
  }

  return arrays;
}

export function getAllDaysOfMonthByWeek(selectedDate: [Date, Date]) {
  const year = selectedDate[0].getFullYear();
  const month = selectedDate[0].getMonth(); // Note: 0-indexed (0 = January, 11 = December)

  const daysInMonth = new Date(year, month + 1, 0).getDate(); // Get total days in the month
  const daysArray: Array<Date> = [];

  // Create an array of all days in the month
  for (let day = 1; day <= daysInMonth; day++) {
    daysArray.push(new Date(year, month, day));
  }

  // Organize days into weeks (Monday as the start of the week)
  const weeks: Array<any> = [];
  let currentWeek: Array<Day> = [];

  for (const day of daysArray) {
    const dayOfWeek = day.getDay(); // Sunday = 0, Monday = 1, ..., Saturday = 6
    const adjustedDayOfWeek = dayOfWeek === 0 ? 7 : dayOfWeek; // Adjust Sunday to be the last day (7)

    // Start a new week if it's Monday and the current week has entries
    if (adjustedDayOfWeek === 1 && currentWeek.length > 0) {
      weeks.push(currentWeek);
      currentWeek = [];
    }

    currentWeek.push(day as any);

    // Push the last week at the end of the loop
    if (day.getDate() === daysInMonth) {
      weeks.push(currentWeek);
    }
  }

  return weeks;
}

/**
 * Find the current entity from selectedEntity or by URL fallback in entities arrays.
 */
function resolveEntity(): any {
  const metadata = store.getState().metadata;
  const selectedEntity = metadata?.selectedEntity as any;

  if (selectedEntity) return selectedEntity;

  // Fallback: selectedEntity not yet loaded, find in entities by URL
  const entities = metadata?.entities as any;
  if (!entities) return null;

  const pathParts = window.location.pathname.split('/');
  const pathSegment = pathParts[1] || '';
  const entityId = pathParts[2];
  if (!entityId) return null;

  if (pathSegment.includes('member')) {
    return entities.member?.find((m: any) => m.memberId === entityId) || null;
  }
  if (pathSegment.includes('site')) {
    return entities.site?.find((s: any) => s.siteId === entityId) || null;
  }
  if (pathSegment.includes('communit')) {
    return entities.community?.find((c: any) => c.communityId === entityId) || null;
  }

  return null;
}

/**
 * Collect all start-date candidates from an entity's fields:
 * startDate, joinDate, changelog[].startDate, siteType[].startDate
 */
function collectStartDates(entity: any, candidates: luxon.DateTime[]) {
  if (!entity) return;

  if (entity.startDate) {
    candidates.push(luxon.DateTime.fromJSDate(new Date(entity.startDate)));
  }
  if (entity.joinDate) {
    candidates.push(luxon.DateTime.fromJSDate(new Date(entity.joinDate)));
  }
  if (entity.changelog?.length > 0) {
    for (const entry of entity.changelog) {
      if (entry.startDate) {
        candidates.push(luxon.DateTime.fromJSDate(new Date(entry.startDate)));
      }
    }
  }
  if (entity.siteType?.length > 0) {
    for (const st of entity.siteType) {
      if (st.startDate) {
        candidates.push(luxon.DateTime.fromJSDate(new Date(st.startDate)));
      }
    }
  }
}

export function getStartDate() {
  const entity = resolveEntity();
  if (!entity) return null;

  const candidates: luxon.DateTime[] = [];

  // Collect dates from the entity itself
  collectStartDates(entity, candidates);

  // For member/community entities with nested sites, also check each site
  if (entity.sites?.length > 0) {
    for (const site of entity.sites) {
      collectStartDates(site, candidates);
    }
  }

  if (candidates.length === 0) return null;

  return luxon.DateTime.min(...(candidates as [luxon.DateTime, ...luxon.DateTime[]]));
}

export function getEndDate() {
  const entity = resolveEntity();
  if (!entity) return null;

  // For entities with nested sites, get the max endDate/leaveDate across sites
  if (entity.sites?.length > 0) {
    const siteDates = entity.sites
      .map((site: any) => site.leaveDate || site.endDate)
      .filter(Boolean)
      .map((d: string) => luxon.DateTime.fromJSDate(new Date(d)));

    if (siteDates.length > 0) {
      return luxon.DateTime.max(...(siteDates as [luxon.DateTime, ...luxon.DateTime[]]));
    }
  }

  // Direct endDate / leaveDate on the entity
  if (entity.endDate) return entity.endDate;
  if (entity.leaveDate) return entity.leaveDate;

  // Last resort: latest endDate from changelog
  if (entity.changelog?.length > 0) {
    const changelogDates = entity.changelog
      .map((entry: any) => entry.endDate)
      .filter(Boolean)
      .map((d: string) => luxon.DateTime.fromJSDate(new Date(d)));

    if (changelogDates.length > 0) {
      return luxon.DateTime.max(...(changelogDates as [luxon.DateTime, ...luxon.DateTime[]]));
    }
  }

  // Fallback: look up endDate from flat entities array
  // (selectedEntity from individual API may not include endDate)
  const metadata = store.getState().metadata;
  const entities = metadata?.entities as any;
  const entityType = metadata?.entityType;

  if (entities && entity) {
    const entityId =
      entity.communityId || entity.memberId || entity.siteId;

    if (entityType === 'community' && entities.community) {
      const found = entities.community.find(
        (c: any) => c.communityId === entityId
      );
      if (found?.endDate) return found.endDate;
    } else if (entityType === 'member' && entities.member) {
      const found = entities.member.find(
        (m: any) => m.memberId === entityId
      );
      if (found?.endDate) return found.endDate;
    } else if (entityType === 'site' && entities.site) {
      const found = entities.site.find(
        (s: any) => s.siteId === entityId
      );
      if (found?.leaveDate) return found.leaveDate;
      if (found?.endDate) return found.endDate;
    }
  }

  return null;
}

/**
 * Returns the effective end date for calendar navigation.
 *
 * It is the minimum between:
 *   - the entity's endDate (if any), and
 *   - the configured app upper bound (from `state.appConfig.calendar.upperBound`,
 *     populated by `/common/v1/config`); falls back to today when no upper
 *     bound is configured.
 *
 * The upper bound is applied uniformly across ALL calendar time sets (day,
 * week, month, year). Each view-specific component is responsible for
 * rounding the returned date to its own granularity (start-of-week,
 * start-of-month, ...) when comparing.
 *
 * `upperBoundOverride` lets callers pass the upper bound explicitly — useful
 * inside React components that already subscribe to the redux state via
 * `useSelector`, so React reactivity is preserved (the store read below
 * would otherwise miss updates between renders).
 */
export function getEffectiveEndDate(upperBoundOverride?: string | null): Date {
  const endDate = getEndDate();
  const rawUpperBound =
    upperBoundOverride !== undefined
      ? upperBoundOverride
      : store.getState()?.appConfig?.calendar?.upperBound || null;
  const configuredUpperBound =
    typeof rawUpperBound === 'string'
      ? luxon.DateTime.fromISO(rawUpperBound).startOf('day')
      : null;
  const upperBound =
    configuredUpperBound?.isValid === true
      ? configuredUpperBound
      : luxon.DateTime.now().startOf('day');

  if (!endDate) {
    return upperBound.toJSDate();
  }

  const entityEndDate = luxon.DateTime.fromJSDate(new Date(endDate)).startOf('day');
  if (!entityEndDate.isValid) {
    return upperBound.toJSDate();
  }

  return luxon.DateTime.min(entityEndDate, upperBound).toJSDate();
}
